import React, { useState } from 'react';

import {
  AdminPayoutStatus as AdminPayoutStatusModel,
  finalizeSettlement,
  getAdminPayoutStatus,
  PayoutOperation,
  ReconciliationStatus,
} from '../api/settlement';
import { formatMoney } from '../../../commodity/format/money';
import {
  Empty,
  Kpi,
  KpiRow,
  Note,
  OpsPage,
  Pill,
  PillTone,
  Row,
  Section,
  Table,
} from '../../../design-system/ops';

/**
 * PI-3 Epic 2 S2.2 — the payout-status lookup.
 *
 * Ops paste an enrollment id and see the per-operation diagnostic: what the
 * enrollment believes happened against what the Payments ACL actually cached.
 * Where they disagree is the whole point of the screen.
 *
 * Rewritten onto the design system. It carried its own status colours, its own
 * pill, its own button factory and its own table borders, none of which the
 * console's theme could reach.
 */

const STATUS_TONE: Record<ReconciliationStatus, PillTone> = {
  ok: 'ok',
  mismatch: 'bad',
  pending: 'neu',
};

const STATUS_LABEL: Record<ReconciliationStatus, string> = {
  ok: 'Cuadra',
  mismatch: 'Discrepa',
  pending: 'Pendiente',
};

/** What the Payments ACL cached for an operation, in words rather than a tick. */
const paymentsSide = (op: PayoutOperation): string => {
  if (!op.paymentsSide.cached) return 'No cacheado';
  return op.paymentsSide.success
    ? 'Cacheado, con éxito'
    : `Falló: ${op.paymentsSide.failureMessage ?? 'sin detalle'}`;
};

const AdminPayoutStatus: React.FC = () => {
  const [enrollmentId, setEnrollmentId] = useState('');
  const [data, setData] = useState<AdminPayoutStatusModel | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [confirmingFinalize, setConfirmingFinalize] = useState(false);

  const load = async () => {
    if (!enrollmentId.trim()) return;
    setLoading(true);
    setMessage(null);
    setData(null);
    try {
      setData(await getAdminPayoutStatus(enrollmentId.trim()));
      setFailed(false);
    } catch (err: any) {
      setFailed(true);
      setMessage(err?.message || 'No se pudo cargar el estado de pago');
    } finally {
      setLoading(false);
    }
  };

  const finalize = async () => {
    if (!enrollmentId.trim()) return;
    setConfirmingFinalize(false);
    setBusy(true);
    setMessage(null);
    try {
      await finalizeSettlement(enrollmentId.trim());
      setFailed(false);
      setMessage('Liquidación finalizada, o ya lo estaba. Es idempotente.');
      await load();
    } catch (err: any) {
      setFailed(true);
      setMessage(err?.message || 'No se pudo finalizar');
    } finally {
      setBusy(false);
    }
  };

  const mismatches =
    data?.operations.filter((o) => o.reconciliationStatus === 'mismatch').length ?? 0;

  return (
    <OpsPage
      title="Estado de pago"
      subtitle="Lo que la inscripción cree que pasó, contra lo que el ACL de Payments tiene cacheado. Donde discrepan está el problema."
    >
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
        <input
          id="payout-enrollment-id"
          className="inp"
          style={{ flex: 1, minWidth: 240, fontFamily: 'ui-monospace, monospace' }}
          aria-label="Id de inscripción"
          value={enrollmentId}
          placeholder="enrollmentId"
          onChange={(e) => setEnrollmentId(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') load();
          }}
        />
        <button className="btn btn-p" onClick={load} disabled={loading}>
          {loading ? 'Buscando…' : 'Buscar'}
        </button>
        <button
          className="btn btn-o"
          onClick={() => setConfirmingFinalize(true)}
          disabled={busy || !enrollmentId.trim()}
        >
          Finalizar liquidación
        </button>
      </div>

      {/* DEC-A01-21: the target and the consequence, stated before the money
          command. Read from the field as it stands now, so changing the id
          after opening this cannot confirm a different enrollment. */}
      {confirmingFinalize ? (
        <div role="group" aria-label="Confirmar la liquidación" style={{ marginBottom: 18 }}>
          <Note tone="warn">
            {`Vas a cerrar la liquidación de la inscripción ${enrollmentId.trim()}. Fija el pago final al profesor con lo registrado ahora.`}
          </Note>
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <button className="btn btn-o" onClick={() => setConfirmingFinalize(false)}>
              Volver
            </button>
            <button className="btn btn-p" onClick={finalize} disabled={busy}>
              Confirmar liquidación
            </button>
          </div>
        </div>
      ) : null}

      {message ? <Note tone={failed ? 'bad' : 'warn'}>{message}</Note> : null}

      {data ? (
        <>
          <KpiRow>
            <Kpi
              label="Reconciliación"
              value={STATUS_LABEL[data.reconciliationStatus]}
              note={mismatches ? `${mismatches} operaciones discrepan` : 'Todo cuadra'}
              tone={data.reconciliationStatus === 'mismatch' ? 'bad' : 'ok'}
              alert={data.reconciliationStatus === 'mismatch'}
            />
            <Kpi
              label="Liberado"
              value={formatMoney(data.breakdown.released, data.breakdown.currency)}
              note="Ya pagado al profesor"
              tone="ok"
            />
            <Kpi
              label="Pendiente"
              value={formatMoney(data.breakdown.pending, data.breakdown.currency)}
              note="Aún en escrow"
              tone="lav"
            />
            <Kpi
              label="Neto"
              value={formatMoney(data.breakdown.netEarned, data.breakdown.currency)}
              note={`Estado: ${data.breakdown.state}`}
            />
          </KpiRow>

          <KpiRow>
            <Kpi
              label="Retención"
              value={formatMoney(data.breakdown.holdback.amount, data.breakdown.currency)}
              note="Holdback del ciclo"
              tone="warn"
            />
            <Kpi
              label="Reembolsado"
              value={formatMoney(data.breakdown.refunded, data.breakdown.currency)}
              note="Devuelto a la familia"
            />
            <Kpi label="Moneda" value={data.breakdown.currency} note="De la inscripción" />
            <Kpi label="Operaciones" value={data.operations.length} note="Claves de operación" />
          </KpiRow>

          <Section
            title="Operaciones"
            count={data.operations.length}
            subtitle="Una fila por clave de operación. La clave es lo que hace idempotente cada llamada al proveedor."
          >
            {data.operations.length === 0 ? (
              <Empty
                icon="💸"
                title="Sin operaciones"
                detail="Esta inscripción no ha movido dinero todavía."
              />
            ) : (
              <Table
                columns="1fr 140px 160px 200px 110px"
                head={['Clave', 'Acción', 'La inscripción dice', 'Payments dice', 'Estado']}
              >
                {data.operations.map((op) => (
                  <Row key={op.operationKey}>
                    <span className="mut" style={{ fontFamily: 'ui-monospace, monospace' }}>
                      {op.operationKey}
                    </span>
                    <span>{op.action}</span>
                    <span>
                      {op.enrollmentSide.expected ? 'Esperada' : 'No esperada'}
                      <br />
                      <span className="mut">
                        {formatMoney(op.enrollmentSide.expectedAmount, data.breakdown.currency)}
                      </span>
                    </span>
                    <span className="mut">{paymentsSide(op)}</span>
                    <Pill tone={STATUS_TONE[op.reconciliationStatus]}>
                      {STATUS_LABEL[op.reconciliationStatus]}
                    </Pill>
                  </Row>
                ))}
              </Table>
            )}
          </Section>
        </>
      ) : null}
    </OpsPage>
  );
};

export default AdminPayoutStatus;
